import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import TaskService, { Task } from '../../services/task.service';
import { NumberTextField } from '../../components/common';
import ProjectService from '../../services/project.service';
import EmployeeService from '../../services/employee.service';

const TaskForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [formData, setFormData] = useState<Task>({
    project: 0,
    title: '',
    status: 'todo',
    priority: 'medium',
    progress: 0,
    actualHours: 0,
  });

  useEffect(() => {
    fetchProjects();
    fetchEmployees();
    if (isEdit && id) {
      fetchTask(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchProjects = async () => {
    try {
      const response = await ProjectService.getAll({ populate: '*' });
      setProjects(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await EmployeeService.getAll();
      setEmployees(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchTask = async (taskId: number) => {
    try {
      const response = await TaskService.getOne(taskId);
      const task = response.data?.data?.attributes;
      if (task) {
        setFormData({
          ...task,
          project: task.project?.data?.id || 0,
          assignedTo: task.assignedTo?.data?.id,
        });
      }
    } catch (error) {
      console.error('Error fetching task:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isEdit && id) {
        await TaskService.update(parseInt(id), formData);
      } else {
        await TaskService.create(formData);
      }
      navigate('/tasks');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Task' : 'Create Task'}
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                required
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                select
                required
                label="Project"
                name="project"
                value={formData.project}
                onChange={handleChange}
              >
                <MenuItem value={0}>Select Project</MenuItem>
                {projects.map((proj) => (
                  <MenuItem key={proj.id} value={proj.id}>
                    {proj.attributes?.projectName}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                select
                label="Assigned To"
                name="assignedTo"
                value={formData.assignedTo || ''}
                onChange={handleChange}
              >
                <MenuItem value="">Unassigned</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.attributes?.firstName} {emp.attributes?.lastName}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                select
                label="Priority"
                name="priority"
                value={formData.priority || 'medium'}
                onChange={handleChange}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </TextField>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                select
                label="Status"
                name="status"
                value={formData.status || 'todo'}
                onChange={handleChange}
              >
                <MenuItem value="todo">To Do</MenuItem>
                <MenuItem value="inProgress">In Progress</MenuItem>
                <MenuItem value="review">Review</MenuItem>
                <MenuItem value="done">Done</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
              </TextField>
              <NumberTextField
                fullWidth
                label="Progress (%)"
                name="progress"
                value={formData.progress || 0}
                onChange={handleChange}
                inputProps={{ min: 0, max: 100 }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Start Date"
                name="startDate"
                type="date"
                value={formData.startDate || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Due Date"
                name="dueDate"
                type="date"
                value={formData.dueDate || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <NumberTextField
                fullWidth
                label="Estimated Hours"
                name="estimatedHours"
                value={formData.estimatedHours || ''}
                onChange={handleChange}
                inputProps={{ step: '0.5', min: '0' }}
              />
              <NumberTextField
                fullWidth
                label="Actual Hours"
                name="actualHours"
                value={formData.actualHours || 0}
                onChange={handleChange}
                inputProps={{ step: '0.5', min: '0' }}
              />
            </Stack>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description"
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/tasks')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default TaskForm;

